import { useState } from "react";

import { Button, Card, Group, Paper, Textarea } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";

import { ZodSchema } from "zod";

import { TextWrapper } from "@/components/TextWrapper";

export interface NoteEditorFormProps<T extends Record<string, unknown>> {
  initialValues: T;
  schema: ZodSchema;
  onSubmit: (values: T) => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
  testIds?: { text: string; cancel: string; submit: string };
  placeholder?: string;
  className?: string;
  bg?: string;
}

export function NoteEditorForm<T extends Record<string, unknown>>({
  initialValues,
  schema,
  onSubmit,
  onCancel,
  submitLabel = "Update",
  testIds,
  placeholder = "Type note here",
  className,
  bg,
}: NoteEditorFormProps<T>) {
  const [topError, setTopError] = useState<string | null>(null);
  const form = useForm<T>({
    initialValues,
    validate: zodResolver(schema),
  });

  const Container = className === "card" ? Card : Paper;

  return (
    <Container bg={bg} className={className}>
      <form
        onSubmit={form.onSubmit(
          async (values) => {
            setTopError(null);
            try {
              await onSubmit(values);
            } catch (err: unknown) {
              setTopError(err instanceof Error ? err.message : "Unknown error");
            }
          },
          (errors) => {
            if (errors[""]) {
              setTopError(errors[""] as string);
            }
          },
        )}
      >
        {topError && (
          <Group>
            <TextWrapper size="sm" c="var(--mantine-color-error)">
              {topError}
            </TextWrapper>
          </Group>
        )}
        <Textarea
          px="xs"
          autosize
          variant="unstyled"
          placeholder={placeholder}
          {...(form.getInputProps("noteText" as string) as unknown as Record<string, unknown>)}
          mb="xs"
          data-testid={testIds?.text}
        />
        <Group p="xs" gap="xs" justify="flex-end">
          <Button
            color="black"
            variant="subtle"
            size="compact-xs"
            onClick={onCancel}
            data-testid={testIds?.cancel}
          >
            Cancel
          </Button>
          <Button
            color="black"
            type="submit"
            variant="outline"
            size="compact-xs"
            data-testid={testIds?.submit}
          >
            {submitLabel}
          </Button>
        </Group>
      </form>
    </Container>
  );
}
